import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  FileText, Receipt, Building2, Users, ClipboardList,
  Search, Plus, ArrowRight, LayoutDashboard, Landmark, Bell,
  Settings, Command, Zap, MessageSquare, Sparkles, Mail,
  ArrowDownToLine, ArrowUpFromLine, CreditCard, FileCheck, Play,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useActiveEntity } from "@/lib/entity-context";
import { useFeatures, isFinancingAvailable, type FeatureFlags } from "@/lib/features";
import { formatCurrency } from "@/lib/utils";
import { FormattedCurrency } from "@/components/ui/formatted-currency";

interface CommandItem {
  id: string;
  label: string;
  sublabel?: string;
  icon: any;
  category: string;
  action: () => void;
  featureKey?: keyof FeatureFlags;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [, setLocation] = useLocation();
  const { t } = useTranslation("common");
  const { activeEntityId } = useActiveEntity();
  const features = useFeatures();

  const entityParam = activeEntityId ? `?entityId=${activeEntityId}` : "";
  const { data: bills = [] } = useQuery<any[]>({ queryKey: ["/api/bills"] });
  const { data: invoices = [] } = useQuery<any[]>({ queryKey: ["/api/invoices"] });
  const { data: vendors = [] } = useQuery<any[]>({ queryKey: ["/api/vendors"] });
  const { data: customers = [] } = useQuery<any[]>({ queryKey: ["/api/customers"] });
  const { data: tasks = [] } = useQuery<any[]>({
    queryKey: ["/api/tasks", "command-palette"],
    queryFn: async () => {
      const res = await fetch("/api/tasks?status=open,in_progress&limit=50", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const go = useCallback((path: string) => {
    setOpen(false);
    setQuery("");
    setLocation(path);
  }, [setLocation]);

  const filterByFeature = (items: CommandItem[]) =>
    items.filter((item) => {
      if (!item.featureKey) return true;
      if (features[item.featureKey] === false) return false;
      if (item.featureKey === "financing" && !isFinancingAvailable(features)) return false;
      if (item.featureKey === "directDebit" && features.directDebitGeoAllowed === false) return false;
      return true;
    });

  const navigationItems: CommandItem[] = filterByFeature([
    { id: "nav-home", label: t("nav.monitor"), icon: LayoutDashboard, category: "nav", action: () => go("/") },
    { id: "nav-ar", label: t("nav.getPaid"), icon: ArrowDownToLine, category: "nav", action: () => go("/ar"), featureKey: "ar" },
    { id: "nav-ap", label: t("nav.payBills"), icon: ArrowUpFromLine, category: "nav", action: () => go("/ap"), featureKey: "ap" },
    { id: "nav-financing", label: t("nav.financing"), icon: Landmark, category: "nav", action: () => go("/financing"), featureKey: "financing" },
    { id: "nav-reminders", label: t("nav.reminders"), icon: Bell, category: "nav", action: () => go("/ar?tab=reminders"), featureKey: "ar" },
    { id: "nav-approvals", label: t("invoiceApproval.queue"), icon: FileCheck, category: "nav", action: () => go("/inbox") },
    { id: "nav-credit-notes", label: t("nav.getPaid") + " — " + t("cmd.creditNotes"), icon: CreditCard, category: "nav", action: () => go("/ar?tab=credit-notes"), featureKey: "ar" },
    { id: "nav-tax-rates", label: t("nav.getPaid") + " — " + t("cmd.taxRates"), icon: Receipt, category: "nav", action: () => go("/settings?section=business&sub=taxRates"), featureKey: "ar" },
    { id: "nav-direct-debit", label: t("nav.getPaid") + " — " + t("cmd.directDebit"), icon: Receipt, category: "nav", action: () => go("/ar?tab=direct-debit"), featureKey: "directDebit" },
    { id: "nav-reconciliation", label: t("nav.getPaid") + " — " + t("cmd.reconciliation"), icon: Receipt, category: "nav", action: () => go("/ar?tab=reconciliation"), featureKey: "reconciliation" },
    { id: "nav-goods-received", label: t("nav.payBills") + " — " + t("cmd.goodsReceived"), icon: Receipt, category: "nav", action: () => go("/ap?tab=goods-received"), featureKey: "apMatching" },
    { id: "nav-settings", label: t("nav.settings"), icon: Settings, category: "nav", action: () => go("/settings") },
  ]);

  const toolItems: CommandItem[] = filterByFeature([
    { id: "tool-automations", label: t("nav.automations"), icon: Zap, category: "tools", action: () => go("/automations"), featureKey: "automations" },
    { id: "tool-automation-runs", label: t("nav.automations") + " — Runs", icon: Play, category: "tools", action: () => go("/automations/runs"), featureKey: "automations" },
    { id: "tool-reports", label: t("nav.aiReports"), icon: Sparkles, category: "tools", action: () => go("/reports"), featureKey: "aiReports" },
    { id: "tool-chat", label: t("nav.chat"), icon: MessageSquare, category: "tools", action: () => go("/chat"), featureKey: "aiChat" },
    { id: "tool-communications", label: t("cmd.goToCommunications"), icon: Mail, category: "tools", action: () => go("/communications"), featureKey: "communications" },
  ]);

  const createItems: CommandItem[] = filterByFeature([
    { id: "create-bill", label: t("cmd.newBill"), icon: Plus, category: "create", action: () => go("/ap/bills/new"), featureKey: "ap" },
    { id: "create-invoice", label: t("cmd.newInvoice"), icon: Plus, category: "create", action: () => go("/ar/invoices/new"), featureKey: "ar" },
    { id: "create-vendor", label: t("cmd.newVendor"), icon: Plus, category: "create", action: () => go("/ap/vendors/new"), featureKey: "ap" },
    { id: "create-customer", label: t("cmd.newCustomer"), icon: Plus, category: "create", action: () => go("/ar/customers/new"), featureKey: "ar" },
    { id: "create-task", label: t("cmd.newTask"), icon: Plus, category: "create", action: () => go("/tasks/new"), featureKey: "tasks" },
  ]);

  const recordItems: CommandItem[] = [
    ...(features.ap !== false ? bills.map((b: any) => ({
      id: `bill-${b.id}`,
      label: b.vendorInvoiceNumber || `Bill`,
      sublabel: `${formatCurrency(Number(b.total), b.currency)} · ${b.status}`,
      icon: FileText,
      category: "bills",
      action: () => go(`/ap/bills/${b.id}`),
    })) : []),
    ...(features.ar !== false ? invoices.map((inv: any) => ({
      id: `inv-${inv.id}`,
      label: inv.invoiceNumber || "Invoice",
      sublabel: `${formatCurrency(Number(inv.total), inv.currency)} · ${inv.status}`,
      icon: Receipt,
      category: "invoices",
      action: () => go(`/ar/invoices/${inv.id}`),
    })) : []),
    ...(features.ap !== false ? vendors.map((v: any) => ({
      id: `vendor-${v.id}`,
      label: v.name,
      sublabel: v.email || "",
      icon: Building2,
      category: "vendors",
      action: () => go(`/ap/vendors/${v.id}`),
    })) : []),
    ...(features.ar !== false ? customers.map((c: any) => ({
      id: `cust-${c.id}`,
      label: c.name,
      sublabel: c.email || "",
      icon: Users,
      category: "customers",
      action: () => go(`/ar/customers/${c.id}`),
    })) : []),
    ...(features.tasks !== false ? tasks.map((tk: any) => ({
      id: `task-${tk.id}`,
      label: tk.name,
      sublabel: tk.status,
      icon: ClipboardList,
      category: "tasks",
      action: () => go("/"),
    })) : []),
  ];

  const allItems = [...createItems, ...navigationItems, ...toolItems, ...recordItems];

  const filtered = query.length === 0
    ? [...createItems, ...navigationItems, ...toolItems]
    : allItems.filter((item) => {
        const q = query.toLowerCase();
        return (
          item.label.toLowerCase().includes(q) ||
          (item.sublabel && item.sublabel.toLowerCase().includes(q)) ||
          item.category.toLowerCase().includes(q)
        );
      });

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && filtered[selectedIndex]) {
      e.preventDefault();
      filtered[selectedIndex].action();
    }
  };

  const categoryLabels: Record<string, string> = {
    create: t("cmd.quickActions"),
    nav: t("cmd.navigation"),
    tools: t("cmd.tools"),
    bills: t("cmd.bills"),
    invoices: t("cmd.invoices"),
    vendors: t("cmd.vendors"),
    customers: t("cmd.customers"),
    tasks: t("cmd.tasks"),
  };

  let lastCategory = "";

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <DialogContent className="p-0 gap-0 w-[calc(100vw-2rem)] max-w-lg overflow-hidden" data-testid="dialog-command-palette">
        <div className="flex items-center border-b px-3 gap-2">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("cmd.placeholder")}
            className="flex-1 min-w-0 border-0 shadow-none focus-visible:ring-0 h-11 text-sm px-0"
            autoFocus
            data-testid="input-command-search"
          />
          <kbd className="hidden sm:inline-flex h-5 items-center rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground shrink-0">
            ESC
          </kbd>
        </div>

        <div className="max-h-[50vh] sm:max-h-[300px] overflow-y-auto py-1" data-testid="list-command-results">
          {filtered.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="text-no-results">
              {t("cmd.noResults")}
            </div>
          )}

          {filtered.map((item, index) => {
            const showCategory = item.category !== lastCategory;
            lastCategory = item.category;

            return (
              <div key={item.id}>
                {showCategory && (
                  <div className="px-3 py-1.5 text-[11px] font-medium text-muted-foreground tracking-wide uppercase">
                    {categoryLabels[item.category] || item.category}
                  </div>
                )}
                <button
                  className={`w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-sm transition-colors ${
                    index === selectedIndex ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-accent/50"
                  }`}
                  onClick={() => item.action()}
                  onMouseEnter={() => setSelectedIndex(index)}
                  data-testid={`cmd-item-${item.id}`}
                >
                  <item.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 text-left min-w-0">
                    <span className="truncate block">{item.label}</span>
                  </div>
                  {item.sublabel && (
                    <span className="text-xs text-muted-foreground truncate max-w-[140px]">{item.sublabel}</span>
                  )}
                  {item.category === "create" && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {t("cmd.create")}
                    </Badge>
                  )}
                  {(item.category === "nav" || item.category === "tools") && (
                    <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <div className="border-t px-3 py-2 flex items-center justify-between text-[11px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">↑↓</kbd>
              {t("cmd.navigate")}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">↵</kbd>
              {t("cmd.select")}
            </span>
          </div>
          <span className="flex items-center gap-1">
            <Command className="h-3 w-3" />K {t("cmd.toToggle")}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CommandPaletteTrigger() {
  const { t } = useTranslation("common");

  return (
    <button
      onClick={() => {
        document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
      }}
      className="hidden md:flex items-center gap-2 px-2.5 py-1 rounded-md border text-xs text-muted-foreground hover:bg-muted transition-colors"
      data-testid="button-command-palette"
    >
      <Search className="h-3 w-3" />
      <span>{t("cmd.search")}</span>
      <kbd className="ml-1 font-mono text-[10px] bg-muted rounded px-1">⌘K</kbd>
    </button>
  );
}
